import React, {FC} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {NewPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {macrosHelpArticleUrl} from '../../../../../helpers/macroHelpers';
import {
  ActivitySourceMacroModel,
  ActivitySourceRuleModel,
  ActivitySourceVisualTypesEnum,
} from '../../../../../models/api/activitySourceModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {getHasPermission} from '../../../../../store/data/access/accessSelectors';
import {ActivityPill} from '../activityPill/activityPill';
import {StyledActivityRulePillLearnMoreDiv, StyledActivityRulePillLinkIcon} from './activityRulePillHelpers';

/*
 * Props.
 */

interface ActivityMacroPillProps {
  panelId?: NewPanelId;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
  onClickTrackingCallback?: () => void;
}

/*
 * Component.
 */

export const ActivityMacroPill: FC<ActivityMacroPillProps> = ({source}) => {
  const {name, namespace, macroId, ruleBundleId} = getPropsFromSource(source);

  const hasPermission = useAppSelector(getHasPermission);
  const {navigateToLink} = useLinkNavigation();
  if (!namespace) {
    return null;
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <ActivityPill icon="lightningTiny" isOpen={isOpen}>
          {name}
        </ActivityPill>
      )}
      renderModal={(popoverProps) => (
        <Dropdown {...popoverProps} width="fit-content">
          <DropdownList {...popoverProps}>
            <SimpleDropdownItem
              isDisabled={!hasPermission(namespace, PermissionsEnum.EDIT_RULE) || !(ruleBundleId || macroId)}
              onSelect={() => {
                navigateToLink(
                  macroId
                    ? `/settings/${namespace}/rules/macros/edit/${macroId}`
                    : `/settings/${namespace}/rules/macros/edit/${ruleBundleId}?isRuleBundleId=true`,
                );
              }}
            >
              <Trans id="activity-rule-macro-pill-edit">Edit macro</Trans>
            </SimpleDropdownItem>
            <SimpleDropdownItem onSelect={() => navigateToLink(macrosHelpArticleUrl)}>
              <StyledActivityRulePillLearnMoreDiv>
                <Trans id="activity-rule-macro-pill-help">
                  Learn more about macros <StyledActivityRulePillLinkIcon name="externalLinkSmall" />
                </Trans>
              </StyledActivityRulePillLearnMoreDiv>
            </SimpleDropdownItem>
          </DropdownList>
        </Dropdown>
      )}
    />
  );
};

/*
 * Helpers.
 */

function getPropsFromSource(source: ActivitySourceRuleModel | ActivitySourceMacroModel) {
  if (source.visualType === ActivitySourceVisualTypesEnum.RULE) {
    return {
      name: source.ruleName,
      namespace: source.ruleNamespace,
      ruleBundleId: source.ruleBundleId,
    };
  }

  return {
    name: source.macroName,
    namespace: source.macroNamespace,
    ruleBundleId: null,
    macroId: source.macroId,
  };
}
